import type { ReactNode } from 'react';

import { QUALITY_ISSUE_TYPE, RISK_ISSUE_TYPE } from '../../../../shared/issuesV2';
import type { IssueType } from '../../../../shared/issuesV2';

type IssueListItemDetails = {
  title: string;
  description?: ReactNode;
  fixable?: boolean;
};

const ISSUE_INDEX: Record<IssueType, IssueListItemDetails> = {
  unlicensed_dependency: {
    title: 'Unlicensed Dependencies', // or: Copyrighted Dependencies
    description: (
      <span>
        This package contains unlicensed code, which by default is copyrighted to the original author. You{' '}
        <b>may not</b> use these dependencies without first obtaining a license.
      </span>
    ),
    fixable: true,
  },
  unconcluded_dependency: {
    title: 'Unconcluded Dependencies',
    description: (
      <span>
        These dependencies have identified licenses but do not have any marked as concluded.
      </span>
    ),
  },
  multi_license_concluded: {
    title: 'Multi-License Conclusions',
    description: (
      <span>
        These dependencies have multi-license conclusions detected.
      </span>
    ),
  },
  policy_conflict: {
    title: 'Denied License Issues',
    description: <span>These licenses conflict with your policy.</span>,
    fixable: true,
  },
  policy_flag: {
    title: 'Flagged License Issues',
    description: (
      <span>
        These licenses can be problematic if used in the wrong ways. You must manually review and approve their usage.
      </span>
    ),
    fixable: true,
  },
  vulnerability: {
    title: 'Vulnerabilities',
    description: (
      <span>
        These dependencies contain security vulnerabilities that could lead to possible exploits within your
        application. Have a technical team member review these.
      </span>
    ),
  },
  outdated_dependency: {
    title: 'Outdated Dependencies',
    description: (
      <span>
        These dependencies have been flagged as outdated by your policy. This may either be an out of date Major, Minor, or Patch version.
      </span>
    ),
    fixable: true,
  },
  blacklisted_dependency: {
    title: 'Blocked Dependencies',
    description: (
      <span>
        These dependencies have been blocked by your policy.
      </span>
    ),
    fixable: true,
  },
  [RISK_ISSUE_TYPE.RiskAbandonware]: {
    title: 'Abandoned Dependencies',
    description: (
      <span>
        These dependencies have not received updates in over two years and may have been abandoned.
      </span>
    ),
    fixable: true,
  },
  [RISK_ISSUE_TYPE.RiskEmptyPackage]: {
    title: 'Empty Dependencies',
    description: (
      <span>
        These dependencies have no reasonable source code and are considered empty.
      </span>
    ),
    fixable: false,
  },
  [RISK_ISSUE_TYPE.RiskNativeCode]: {
    title: 'Native Code Detected',
    description: (
      <span>
        These dependencies contain compiled executables that are not expected within their ecosystem.
      </span>
    ),
    fixable: false,
  },
  [QUALITY_ISSUE_TYPE.Malware]: {
    title: 'Malware',
    description: (
      <span>Malicious Dependency Identified</span>
    ),
    fixable: false,
  },
};

export default ISSUE_INDEX;
